import React, {
  useState,
  useRef,
  useEffect,
  useCallback
} from 'react';
import { useLocation, Routes, Route } from 'react-router-dom';
import musicNoteIcon from './music-note.svg';
import './App.css';
import * as Tone from 'tone';
import { Midi } from '@tonejs/midi';
import Navbar from './Navbar';
import ResetPasswordPage from './components/ResetPasswordPage';
import { trackEvent } from './analytics';
import {
  ChakraProvider,
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Progress,
  Image,
  Container,
  HStack,
  Link,
  useToast,
  extendTheme,
} from '@chakra-ui/react';
import { FaDownload, FaPlay, FaShare } from 'react-icons/fa';

const DOMAIN = 'https://pyaar.ai';
// const DOMAIN = 'http://142.170.89.112:62122';
const BACKEND_STATUS_MESSAGE = 'Backend is offline. Uploads and transcriptions are temporarily unavailable.';

// Create a custom theme with enhanced styling
const theme = extendTheme({
  styles: {
    global: {
      body: {
        bg: 'linear-gradient(180deg, #0a0a1f 0%, #1a0a2e 50%, #16003b 100%)',
        color: 'white',
      },
      '*': {
        scrollBehavior: 'smooth',
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: 'bold',
        borderRadius: '12px',
        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      },
      variants: {
        solid: {
          _hover: {
            transform: 'translateY(-3px)',
            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.6)',
          },
          _active: {
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    Progress: {
      baseStyle: {
        filledTrack: {
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        },
      },
    },
  },
});

function App() {
  const [audio_midi_id, setAudioMidiId] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('Upload an MP3');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const [midi, setMidi] = useState(null);
  const [backendUnavailable, setBackendUnavailable] = useState(false);
  const workflowStartedAtRef = useRef(null);
  const uploadSourceRef = useRef(null);
  const transcriptionSuccessTrackedRef = useRef(false);
  const backendUnavailableTrackedRef = useRef(false);
  const pollTimeoutRef = useRef(null);
  const progressTargetRef = useRef(0);
  const toast = useToast();

  const location = useLocation();

  const getElapsedSeconds = useCallback(() => {
    if (!workflowStartedAtRef.current) return null;
    return Math.round((Date.now() - workflowStartedAtRef.current) / 1000);
  }, []);

  const clearStatusPoll = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const setProgressTarget = useCallback((nextTarget) => {
    const clamped = Math.max(0, Math.min(100, nextTarget));
    progressTargetRef.current = clamped;
  }, []);

  useEffect(() => {
    if (!isLoading) return undefined;

    const intervalId = setInterval(() => {
      setProgress((previous) => {
        const target = progressTargetRef.current;
        if (previous >= target) return previous;
        const step = Math.max(0.5, (target - previous) * 0.22);
        const nextValue = Math.min(target, previous + step);
        return Number(nextValue.toFixed(2));
      });
    }, 140);

    return () => clearInterval(intervalId);
  }, [isLoading]);

  useEffect(() => () => clearStatusPoll(), [clearStatusPoll]);

  useEffect(() => {
    if (location.pathname === '/') {
      trackEvent('landing_page_view', { page_path: location.pathname });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (backendUnavailable && !backendUnavailableTrackedRef.current) {
      trackEvent('backend_unavailable_shown', { page_path: location.pathname });
      backendUnavailableTrackedRef.current = true;
    }

    if (!backendUnavailable) {
      backendUnavailableTrackedRef.current = false;
    }
  }, [backendUnavailable, location.pathname]);

  // Health check: ping the backend to detect if it's reachable
  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    fetch(`${DOMAIN}/transcribe/`, { method: 'HEAD', signal: controller.signal })
      .then(res => {
        clearTimeout(timeoutId);
        // 502/503/504 means nginx couldn't reach the upstream
        setBackendUnavailable(res.status >= 502 && res.status <= 504);
      })
      .catch(() => {
        clearTimeout(timeoutId);
        setBackendUnavailable(true);
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const playMidi = midi => {
    const synth = new Tone.PolySynth(Tone.Synth).toDestination();
    Tone.start();

    midi.tracks.forEach(track => {
      track.notes.forEach(note => {
        const startTime = Tone.now() + note.time;
        synth.triggerAttackRelease(note.name, note.duration, startTime, note.velocity);
      });
    });
  };

  const sendClickLocationEvent = click_location => {
    trackEvent('click', {
      click_location,
      page_path: location.pathname,
    });
  };

  const handleUploadActivation = () => {
    if (backendUnavailable) {
      trackEvent('blocked_backend_unavailable', {
        action: 'upload_activation',
      });
      toast({
        title: 'Service offline',
        description: BACKEND_STATUS_MESSAGE,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    sendClickLocationEvent('click_music_icon');
    fileInputRef.current.click();
  };

  const handleUpload = async (formData, endpoint, source) => {
    if (backendUnavailable) {
      trackEvent('blocked_backend_unavailable', {
        action: 'upload_submit',
        upload_source: source,
      });
      toast({
        title: 'Service offline',
        description: BACKEND_STATUS_MESSAGE,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    workflowStartedAtRef.current = Date.now();
    uploadSourceRef.current = source;
    transcriptionSuccessTrackedRef.current = false;
    trackEvent('upload_started', { upload_source: source });
    setIsLoading(true);
    setProgress(2);
    setProgressTarget(8);
    setLoadingMessage('Processing upload...');

    try {
      const uploadResponse = await fetch(endpoint, { method: 'POST', body: formData });
      const uploadResult = await uploadResponse.json();
      if (!uploadResponse.ok) throw new Error(uploadResult.message || 'Failed to upload.');

      console.log('Upload result:', uploadResult);
      if (uploadResult.audio_midi_id) {
        setAudioMidiId(uploadResult.audio_midi_id);
        trackEvent('upload_succeeded', {
          upload_source: source,
          audio_midi_id: String(uploadResult.audio_midi_id),
        });
        startMidiGeneration(uploadResult.audio_midi_id, source);
      } else {
        throw new Error('ID is missing from the upload response');
      }
    } catch (error) {
      console.error('Error in upload:', error);
      trackEvent('upload_error', {
        upload_source: source,
        error_message: (error.message || 'upload_failed').slice(0, 180),
      });
      setError(error.message);
      setIsLoading(false);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const resetState = () => {
    clearStatusPoll();
    setAudioMidiId(null);
    setDownloadUrl(null);
    setLoadingMessage('Upload an MP3');
    setIsLoading(false);
    setProgress(0);
    setError('');
    setMidi(null);
    setProgressTarget(0);
    uploadSourceRef.current = null;
    workflowStartedAtRef.current = null;
    transcriptionSuccessTrackedRef.current = false;
  };

  const startMidiGeneration = async (id, source = uploadSourceRef.current || 'unknown') => {
    setProgressTarget(14);
    trackEvent('transcription_started', {
      upload_source: source,
      audio_midi_id: String(id),
    });
    setLoadingMessage('Generating MIDI file...');
    const formData = new FormData();
    formData.append('audio_midi_id', id);
    formData.append('audio_chunk_length', 15);

    try {
      // Begin polling immediately; do not wait for generate response,
      // because some backends keep this request open until transcription ends.
      clearStatusPoll();
      pollTimeoutRef.current = setTimeout(() => checkTranscriptionStatus(id), 400);

      const generateResponse = await fetch(`${DOMAIN}/transcribe/generate/`, {
        method: 'POST',
        body: formData,
      });
      let generateResult = {};
      try {
        generateResult = await generateResponse.json();
      } catch (_error) {
        generateResult = {};
      }
      if (!generateResponse.ok) throw new Error(generateResult.message || 'Failed to generate MIDI.');
    } catch (error) {
      console.error('Error in MIDI generation:', error);
      clearStatusPoll();
      trackEvent('transcription_error', {
        upload_source: source,
        audio_midi_id: String(id),
        stage: 'generate',
        error_message: (error.message || 'generation_failed').slice(0, 180),
      });
      setError(error.message);
      setIsLoading(false);
      setProgressTarget(0);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleMp3Select = async event => {
    if (backendUnavailable) {
      trackEvent('blocked_backend_unavailable', {
        action: 'mp3_select',
      });
      toast({
        title: 'Service offline',
        description: BACKEND_STATUS_MESSAGE,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      event.target.value = null;
      return;
    }
    resetState();
    const file = event.target.files[0];
    if (!file || file.type !== 'audio/mpeg' || file.size > 20000000) {
      trackEvent('upload_validation_failed', {
        upload_source: 'mp3',
        reason: 'invalid_mp3_or_size',
        file_type: file?.type || 'none',
        file_size_bytes: file?.size || 0,
      });
      setError('Please ensure the file is an MP3 and is less than 20 MB.');
      toast({
        title: 'Error',
        description: 'Please ensure the file is an MP3 and is less than 20 MB.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    sendClickLocationEvent('upload_audio_icon');

    const formData = new FormData();
    formData.append('audio', file);
    handleUpload(formData, `${DOMAIN}/transcribe/upload/`, 'mp3');
    event.target.value = null;
  };

  const checkTranscriptionStatus = useCallback(async (audioId) => {
    try {
      clearStatusPoll();
      const response = await fetch(`${DOMAIN}/transcribe/status/${audioId}/`);
      const statusData = await response.json();
      if (statusData.status === 'completed') {
        setProgressTarget(100);
        setProgress(100);
        const midiPreviewUrl = `${DOMAIN}/transcribe/midi/${audioId}/`;
        const midiDownloadUrl = `${DOMAIN}/transcribe/download_midi/${audioId}/`;
        setDownloadUrl(midiDownloadUrl);
        setLoadingMessage('Transcription complete!');
        setIsLoading(false);

        if (!transcriptionSuccessTrackedRef.current) {
          transcriptionSuccessTrackedRef.current = true;
          trackEvent('transcription_succeeded', {
            upload_source: uploadSourceRef.current || 'unknown',
            audio_midi_id: String(audioId),
            elapsed_seconds: getElapsedSeconds(),
          });
        }

        const midiResponse = await fetch(midiPreviewUrl);
        if (!midiResponse.ok) {
          throw new Error('MIDI preview file is not available.');
        }
        const midiArrayBuffer = await midiResponse.arrayBuffer();
        const midi = new Midi(midiArrayBuffer);
        setMidi(midi);

        setTimeout(() => {
          setLoadingMessage('Upload an MP3 to begin');
        }, 4000);
      } else {
        const currentChunk = Number(
          statusData.current_chunk ??
          statusData.current_segment ??
          0
        );
        const totalChunks = Number(
          statusData.total_chunks ??
          statusData.num_transcription_segments ??
          0
        );
        const serverProgress =
          totalChunks > 0 ? (currentChunk / totalChunks) * 100 : null;
        const boundedServerProgress =
          serverProgress === null ? null : Math.max(0, Math.min(95, serverProgress));

        if (boundedServerProgress !== null) {
          setProgressTarget(Math.max(progressTargetRef.current, boundedServerProgress));
          setLoadingMessage(
            `Transcribing chunk ${Math.min(currentChunk, totalChunks)} of ${totalChunks} (${Math.round(boundedServerProgress)}%)`
          );
        } else if (statusData.status === 'queued' || statusData.status === 'pending') {
          setProgressTarget(Math.max(progressTargetRef.current, 10));
          setLoadingMessage('Queued for transcription...');
        } else {
          setProgressTarget(Math.min(95, Math.max(progressTargetRef.current + 2, 18)));
          setLoadingMessage('Transcribing audio...');
        }

        pollTimeoutRef.current = setTimeout(() => checkTranscriptionStatus(audioId), 2500);
      }
    } catch (error) {
      console.error('Failed to fetch transcription status or MIDI file:', error);
      trackEvent('transcription_error', {
        upload_source: uploadSourceRef.current || 'unknown',
        audio_midi_id: String(audioId),
        stage: 'status_poll',
        error_message: 'status_or_midi_fetch_failed',
      });
      setError('Failed to fetch transcription status or MIDI file.');
      setIsLoading(false);
      setProgressTarget(0);
      toast({
        title: 'Error',
        description: 'Failed to fetch transcription status or MIDI file.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [toast, getElapsedSeconds, clearStatusPoll, setProgressTarget]);

  const downloadMidi = () => {
    if (!downloadUrl) {
      trackEvent('download_error', {
        upload_source: uploadSourceRef.current || 'unknown',
        reason: 'missing_download_url',
      });
      return;
    }

    sendClickLocationEvent('download_midi');
    trackEvent('download_midi_success', {
      upload_source: uploadSourceRef.current || 'unknown',
      audio_midi_id: audio_midi_id ? String(audio_midi_id) : 'unknown',
      elapsed_seconds: getElapsedSeconds(),
    });
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', 'converted.midi');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const playMidiHandler = () => {
    sendClickLocationEvent('play_midi');
    trackEvent('midi_preview_played', {
      upload_source: uploadSourceRef.current || 'unknown',
      audio_midi_id: audio_midi_id ? String(audio_midi_id) : 'unknown',
    });
    playMidi(midi);
  };

  const shareMidiHandler = () => {
    if (midi && downloadUrl) {
      const shareUrl = `${window.location.origin}?sharedMidiId=${audio_midi_id}`;
      trackEvent('share_clicked', {
        upload_source: uploadSourceRef.current || 'unknown',
        audio_midi_id: audio_midi_id ? String(audio_midi_id) : 'unknown',
      });
      navigator.clipboard.writeText(shareUrl).then(() => {
        toast({
          title: 'Success',
          description: 'Shareable URL copied to clipboard!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sharedMidiId = params.get('sharedMidiId');
    if (sharedMidiId) {
      trackEvent('shared_link_opened', { audio_midi_id: String(sharedMidiId) });
      uploadSourceRef.current = 'shared_link';
      workflowStartedAtRef.current = Date.now();
      if (backendUnavailable) {
        trackEvent('blocked_backend_unavailable', {
          action: 'shared_link_open',
        });
        setError(BACKEND_STATUS_MESSAGE);
        return;
      }
      checkTranscriptionStatus(sharedMidiId);
    }
  }, [location.search, checkTranscriptionStatus, backendUnavailable]);

  return (
    <ChakraProvider theme={theme}>
      <Box className="App">
        <Navbar />
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/" element={
            <Container maxW="container.xl" py={10}>
              <VStack spacing={8} align="center">
                {backendUnavailable && (
                  <Box
                    width="100%"
                    maxW="720px"
                    bg="orange.400"
                    color="black"
                    px={6}
                    py={4}
                    borderRadius="lg"
                    boxShadow="md"
                  >
                    <Heading size="md" mb={2}>Service offline</Heading>
                    <Text fontSize="md">{BACKEND_STATUS_MESSAGE}</Text>
                  </Box>
                )}
                {!error ? (
                  <Heading
                    size="2xl"
                    className="animated"
                    bgGradient="linear(to-r, #667eea, #764ba2)"
                    bgClip="text"
                    fontWeight="extrabold"
                  >
                    {loadingMessage}
                  </Heading>
                ) : (
                  <Text
                    fontSize="2xl"
                    className="animated"
                    bgGradient="linear(to-r, #ff6b6b, #ee5a6f)"
                    bgClip="text"
                    fontWeight="bold"
                  >
                    {error}
                  </Text>
                )}
                <Box
                  as="div"
                  position="relative"
                  _hover={backendUnavailable ? {} : { transform: 'scale(1.05)' }}
                  transition="all 0.3s"
                  role="button"
                  aria-label="Upload MP3 file"
                  aria-disabled={backendUnavailable}
                  tabIndex={0}
                  cursor={backendUnavailable ? 'not-allowed' : 'pointer'}
                  opacity={backendUnavailable ? 0.6 : 1}
                  onClick={handleUploadActivation}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleUploadActivation();
                    }
                  }}
                >
                  <Image
                    src={musicNoteIcon}
                    alt="Upload music file - Click to select an MP3 file from your computer"
                    className={`App-logo ${isLoading ? 'pulseRotate' : ''}`}
                    boxSize="180px"
                    cursor="inherit"
                    filter="drop-shadow(0 0 30px rgba(102, 126, 234, 0.4))"
                  />
                </Box>
                
                <input
                  type="file"
                  accept=".mp3"
                  onChange={handleMp3Select}
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                />
                
                {downloadUrl && (
                  <HStack
                    spacing={4}
                    flexWrap="wrap"
                    justifyContent="center"
                    width="100%"
                    maxW="600px"
                  >
                    <Button
                      leftIcon={<FaDownload />}
                      onClick={downloadMidi}
                      size={{ base: "md", md: "lg" }}
                      bgGradient="linear(to-r, #4facfe, #00f2fe)"
                      _hover={{
                        bgGradient: 'linear(to-r, #00f2fe, #4facfe)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(79, 172, 254, 0.6)',
                      }}
                      flex={{ base: "1 1 100%", sm: "1 1 auto" }}
                    >
                      Download MIDI
                    </Button>
                    <Button
                      leftIcon={<FaPlay />}
                      onClick={playMidiHandler}
                      size={{ base: "md", md: "lg" }}
                      bgGradient="linear(to-r, #11998e, #38ef7d)"
                      _hover={{
                        bgGradient: 'linear(to-r, #38ef7d, #11998e)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(56, 239, 125, 0.6)',
                      }}
                      flex={{ base: "1 1 45%", sm: "1 1 auto" }}
                    >
                      Play MIDI
                    </Button>
                    <Button
                      leftIcon={<FaShare />}
                      onClick={shareMidiHandler}
                      size={{ base: "md", md: "lg" }}
                      bgGradient="linear(to-r, #667eea, #764ba2)"
                      _hover={{
                        bgGradient: 'linear(to-r, #764ba2, #667eea)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(118, 75, 162, 0.6)',
                      }}
                      flex={{ base: "1 1 45%", sm: "1 1 auto" }}
                    >
                      Share MIDI
                    </Button>
                  </HStack>
                )}

                {isLoading && (
                  <Box width="100%" maxW="600px">
                    <Progress
                      value={progress}
                      size="lg"
                      width="100%"
                      hasStripe
                      isAnimated
                      sx={{
                        '& > div': {
                          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                        },
                      }}
                      bg="whiteAlpha.100"
                      borderRadius="full"
                    />
                  </Box>
                )}

                <Text
                  fontSize="2xl"
                  className={isLoading ? 'hidden' : 'typewriter'}
                  bgGradient="linear(to-r, #667eea, #764ba2, #f093fb)"
                  bgClip="text"
                  fontWeight="semibold"
                  mt={4}
                >
                  Turn any song into MIDI — free, no signup required
                </Text>
              </VStack>

              <Box mt={20} mb={10}>
                <VStack spacing={16} align="stretch">
                  <Box
                    id="home"
                    textAlign="center"
                    p={10}
                    borderRadius="2xl"
                    bg="whiteAlpha.50"
                    backdropFilter="blur(10px)"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <Heading
                      as="h1"
                      size="2xl"
                      mb={6}
                      bgGradient="linear(to-r, #667eea, #764ba2)"
                      bgClip="text"
                    >
                      Turn Any Song Into MIDI
                    </Heading>
                    <Text fontSize="xl" color="whiteAlpha.800" lineHeight="tall">
                      Pyaar AI listens to your music and transcribes every note. Upload an MP3 or
                      paste a YouTube link and download a MIDI file in minutes. No signup required.
                    </Text>
                  </Box>

                  <Box id="features">
                    <Heading
                      as="h2"
                      size="2xl"
                      mb={8}
                      textAlign="center"
                      bgGradient="linear(to-r, #f093fb, #f5576c)"
                      bgClip="text"
                    >
                      Features
                    </Heading>
                    <VStack spacing={6}>
                      <Box
                        className="feature-card"
                        p={8}
                        borderRadius="2xl"
                        bg="whiteAlpha.50"
                        backdropFilter="blur(10px)"
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        transition="all 0.3s"
                        _hover={{
                          transform: 'translateY(-5px)',
                          borderColor: 'purple.400',
                          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.4)',
                        }}
                      >
                        <Heading size="md" mb={3} color="purple.300">
                          🎯 High-Accuracy Transcription
                        </Heading>
                        <Text fontSize="lg" color="whiteAlpha.800">
                          Powered by Google{' '}
                          <Link
                            href="https://research.google/pubs/mt3-multi-task-multitrack-music-transcription/"
                            color="blue.400"
                            isExternal
                            textDecoration="underline"
                          >
                            MT3
                          </Link>
                          {' '}for professional-grade music transcription
                        </Text>
                      </Box>

                      <Box
                        className="feature-card"
                        p={8}
                        borderRadius="2xl"
                        bg="whiteAlpha.50"
                        backdropFilter="blur(10px)"
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        transition="all 0.3s"
                        _hover={{
                          transform: 'translateY(-5px)',
                          borderColor: 'blue.400',
                          boxShadow: '0 10px 40px rgba(79, 172, 254, 0.4)',
                        }}
                      >
                        <Heading size="md" mb={3} color="blue.300">
                          🎵 Multiple Format Support
                        </Heading>
                        <Text fontSize="lg" color="whiteAlpha.800">
                          Upload MP3 files directly or paste YouTube URLs - we handle the rest
                        </Text>
                      </Box>

                      <Box
                        className="feature-card"
                        p={8}
                        borderRadius="2xl"
                        bg="whiteAlpha.50"
                        backdropFilter="blur(10px)"
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        transition="all 0.3s"
                        _hover={{
                          transform: 'translateY(-5px)',
                          borderColor: 'green.400',
                          boxShadow: '0 10px 40px rgba(56, 239, 125, 0.4)',
                        }}
                      >
                        <Heading size="md" mb={3} color="green.300">
                          ⚡ Lightning Fast Processing
                        </Heading>
                        <Text fontSize="lg" color="whiteAlpha.800">
                          Get your MIDI files in minutes with real-time progress tracking
                        </Text>
                      </Box>

                      <Box
                        className="feature-card"
                        p={8}
                        borderRadius="2xl"
                        bg="whiteAlpha.50"
                        backdropFilter="blur(10px)"
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        transition="all 0.3s"
                        _hover={{
                          transform: 'translateY(-5px)',
                          borderColor: 'pink.400',
                          boxShadow: '0 10px 40px rgba(240, 147, 251, 0.4)',
                        }}
                      >
                        <Heading size="md" mb={3} color="pink.300">
                          💾 Easy Download & Share
                        </Heading>
                        <Text fontSize="lg" color="whiteAlpha.800">
                          Download your MIDI files instantly or share them with a unique link
                        </Text>
                      </Box>
                    </VStack>
                  </Box>

                  <Box
                    id="who"
                    p={10}
                    borderRadius="2xl"
                    bg="whiteAlpha.50"
                    backdropFilter="blur(10px)"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <Heading
                      as="h2"
                      size="2xl"
                      mb={8}
                      textAlign="center"
                      bgGradient="linear(to-r, #11998e, #38ef7d)"
                      bgClip="text"
                    >
                      Who Is This For?
                    </Heading>
                    <VStack spacing={4} align="stretch">
                      {[
                        { label: 'Musicians', desc: 'Learn how any song is played by seeing every note laid out in MIDI.' },
                        { label: 'Producers', desc: 'Extract MIDI from samples and reference tracks to use in your DAW.' },
                        { label: 'Songwriters', desc: 'Figure out the chords and melody of any recording instantly.' },
                        { label: 'Students', desc: 'Study music theory and train your ear with accurate transcriptions.' },
                      ].map((item) => (
                        <HStack key={item.label} spacing={4} align="start">
                          <Text fontSize="lg" fontWeight="bold" color="green.300" minW="120px">
                            {item.label}
                          </Text>
                          <Text fontSize="lg" color="whiteAlpha.800">
                            {item.desc}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>

                  <Box
                    id="faq"
                    p={10}
                    borderRadius="2xl"
                    bg="whiteAlpha.50"
                    backdropFilter="blur(10px)"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <Heading
                      as="h2"
                      size="2xl"
                      mb={8}
                      textAlign="center"
                      bgGradient="linear(to-r, #f093fb, #f5576c)"
                      bgClip="text"
                    >
                      Frequently Asked Questions
                    </Heading>
                    <VStack spacing={6} align="stretch">
                      {[
                        {
                          q: 'How accurate is AI music transcription?',
                          a: "Pyaar AI uses Google's MT3 model, one of the most accurate multi-instrument transcription systems available. Accuracy is highest on clean recordings with clearly separated instruments. Complex mixes with heavy distortion or overlapping parts may require some manual cleanup.",
                        },
                        {
                          q: 'What audio formats are supported?',
                          a: 'You can upload MP3 files up to 20 MB, or paste any YouTube URL and Pyaar AI will extract the audio automatically.',
                        },
                        {
                          q: 'Is Pyaar AI free?',
                          a: 'Yes. You can transcribe songs to MIDI for free with no account required.',
                        },
                        {
                          q: 'What can I do with the MIDI file?',
                          a: 'Open it in any DAW (Ableton, Logic, FL Studio, GarageBand), notation software (MuseScore, Finale), or MIDI player. Edit individual notes, change instruments, adjust tempo, or use it as a starting point for your own arrangements.',
                        },
                        {
                          q: 'Can I transcribe a YouTube video?',
                          a: 'Yes. Paste any YouTube URL and Pyaar AI will download the audio and transcribe it to MIDI automatically.',
                        },
                      ].map((item) => (
                        <Box key={item.q}>
                          <Heading as="h3" size="md" mb={2} color="purple.300">
                            {item.q}
                          </Heading>
                          <Text fontSize="lg" color="whiteAlpha.800">
                            {item.a}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>

                  <Box
                    id="contact"
                    textAlign="center"
                    p={10}
                    borderRadius="2xl"
                    bg="whiteAlpha.50"
                    backdropFilter="blur(10px)"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <Heading
                      as="h2"
                      size="2xl"
                      mb={6}
                      bgGradient="linear(to-r, #4facfe, #00f2fe)"
                      bgClip="text"
                    >
                      Get in Touch
                    </Heading>
                    <Text fontSize="xl" color="whiteAlpha.800">
                      Have questions or feedback? We'd love to hear from you!
                    </Text>
                    <Link
                      href="mailto:support@pyaar.ai"
                      fontSize="2xl"
                      color="blue.400"
                      fontWeight="bold"
                      mt={4}
                      display="inline-block"
                      _hover={{
                        color: 'blue.300',
                        transform: 'scale(1.05)',
                      }}
                      transition="all 0.3s"
                    >
                      support@pyaar.ai
                    </Link>
                  </Box>
                </VStack>
              </Box>
            </Container>
          } />
        </Routes>
      </Box>
    </ChakraProvider>
  );
}

export default App;
